<template>
	<section class="section-card">
		<div class="section-head">
			<h2 class="section-title">二、指标偏离点</h2>
		</div>
		<p class="section-desc">当前各指标距历史基准值（±2σ）的偏离情况。偏高标红，偏低标蓝，正常标绿。</p>

		<div class="deviation-grid">
			<div class="deviation-card" v-for="item in deviations" :key="item.key" :class="getStatusClass(item)">
				<div class="deviation-card__header">
					<div class="deviation-card__icon" :class="[item.type, getStatusClass(item)]">
						<i :class="getIcon(item.type)"></i>
					</div>
					<span class="deviation-card__label">{{ item.label }}</span>
					<span class="deviation-card__status" :class="getStatusClass(item)">
						{{ getStatusText(item) }}
					</span>
				</div>

				<div class="deviation-card__value-row">
					<span class="deviation-card__value">
						{{ item.value }}
						<small v-if="item.unit">{{ item.unit }}</small>
					</span>
				</div>

				<div class="deviation-card__baseline">
					<span class="baseline-label">历史基准值 ±2σ</span>
					<div class="baseline-bar">
						<div class="baseline-bar__track">
							<div class="baseline-bar__range" :style="getRangeStyle(item)"></div>
							<div
								class="baseline-bar__pointer"
								:class="getStatusClass(item)"
								:style="getPointerStyle(item)"
							></div>
						</div>
						<div class="baseline-bar__labels">
							<span>-2σ</span>
							<span>均值</span>
							<span>+2σ</span>
						</div>
					</div>
				</div>

				<div class="deviation-card__meta">
					<span>
						均值
						<strong>{{ item.historyMean }}</strong>
					</span>
					<span>
						σ
						<strong>{{ item.historySigma }}</strong>
					</span>
					<span>
						偏差
						<strong :class="getStatusClass(item)">{{ formatDeviation(item.deviation) }}</strong>
					</span>
				</div>
			</div>
		</div>
	</section>
</template>

<script>
export default {
	name: 'DeviationSection',
	props: {
		profileData: {
			type: Object,
			default: () => ({}),
		},
	},
	computed: {
		deviations() {
			return (this.profileData && this.profileData.deviations) || []
		},
	},
	methods: {
		getIcon(type) {
			const map = {
				pressure: 'el-icon-data-line',
				flow: 'el-icon-data-board',
				temperature: 'el-icon-sunny',
			}
			return map[type] || 'el-icon-data-line'
		},
		formatDeviation(value) {
			if (value === null || value === undefined) return '--'
			const prefix = value > 0 ? '+' : ''
			return `${prefix}${value.toFixed(2)}σ`
		},
		getStatusText(item) {
			if (!item || item.status !== 'abnormal') return '正常'
			if (item.direction === 'up') return '偏高'
			if (item.direction === 'down') return '偏低'
			return item.displayLabel || '偏离'
		},
		getStatusClass(item) {
			if (!item || item.status !== 'abnormal') return 'normal'
			return item.direction || 'abnormal'
		},
		getRangeStyle() {
			return { left: '10%', width: '80%' }
		},
		getPointerStyle(item) {
			const deviation = item.deviation || 0
			const clamped = Math.max(-3, Math.min(3, deviation))
			const percent = 50 + (clamped / 3) * 40
			return { left: `${percent}%` }
		},
	},
}
</script>

<style lang="scss" scoped>
.section-card {
	position: relative;
	padding: 22px 24px 18px;
	background: var(--panel-bg);
	border: 1px solid var(--panel-border);
	border-radius: 12px;
	box-shadow: var(--panel-shadow);
	backdrop-filter: blur(12px);
}

.section-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 8px;
}

.section-title {
	margin: 0;
	color: var(--title);
	font-size: 17px;
	font-weight: 700;
	letter-spacing: 0.01em;
}

.section-desc {
	margin: 0 0 16px;
	font-size: 13px;
	color: var(--muted);
	line-height: 1.6;
}

.deviation-grid {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 14px;
}

.deviation-card {
	border-radius: 10px;
	border: 1px solid var(--line);
	padding: 16px;
	background: #fff;
	display: flex;
	flex-direction: column;
	gap: 12px;

	&.up,
	&.abnormal {
		border-color: #fecaca;
		background: #fff5f5;
	}

	&.down {
		border-color: #bfdbfe;
		background: #eff6ff;
	}

	&.normal {
		border-color: #bbf7d0;
		background: #f0fdf4;
	}
}

.deviation-card__header {
	display: flex;
	align-items: center;
	gap: 8px;
}

.deviation-card__icon {
	width: 28px;
	height: 28px;
	border-radius: 6px;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;

	&.pressure {
		background: #dbeafe;
		i {
			color: #2563eb;
		}
	}
	&.flow {
		background: #dcfce7;
		i {
			color: #16a34a;
		}
	}
	&.temperature {
		background: #fef3c7;
		i {
			color: #f59e0b;
		}
	}
	&.up {
		background: #fee2e2;
		i {
			color: #dc2626;
		}
	}
	&.down {
		background: #dbeafe;
		i {
			color: #2563eb;
		}
	}
	&.normal {
		background: #dcfce7;
		i {
			color: #16a34a;
		}
	}

	i {
		font-size: 14px;
	}
}

.deviation-card__label {
	font-size: 14px;
	font-weight: 600;
	color: var(--title);
	flex: 1;
	min-width: 0;
}

.deviation-card__status {
	font-size: 12px;
	font-weight: 600;
	padding: 2px 8px;
	border-radius: 8px;
	flex-shrink: 0;

	&.up {
		color: #dc2626;
		background: #fee2e2;
	}
	&.down {
		color: #2563eb;
		background: #dbeafe;
	}
	&.normal {
		color: #16a34a;
		background: #dcfce7;
	}
}

.deviation-card__value-row {
	display: flex;
	align-items: baseline;
	justify-content: space-between;
}

.deviation-card__value {
	font-size: 24px;
	font-weight: 700;
	color: var(--title);

	small {
		font-size: 12px;
		font-weight: 400;
		color: var(--muted);
		margin-left: 2px;
	}
}

.deviation-card__baseline {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.baseline-label {
	font-size: 11px;
	color: var(--muted);
}

.baseline-bar__track {
	position: relative;
	height: 6px;
	background: #f1f5f9;
	border-radius: 3px;
	overflow: visible;
}

.baseline-bar__range {
	position: absolute;
	top: 0;
	height: 100%;
	background: rgba(37, 99, 235, 0.15);
	border-radius: 3px;
}

.baseline-bar__pointer {
	position: absolute;
	top: 50%;
	width: 10px;
	height: 10px;
	border-radius: 50%;
	transform: translate(-50%, -50%);
	border: 2px solid #fff;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
	z-index: 1;

	&.up {
		background: #dc2626;
	}
	&.down {
		background: #2563eb;
	}
	&.normal {
		background: #16a34a;
	}
}

.baseline-bar__labels {
	display: flex;
	justify-content: space-between;
	margin-top: 2px;

	span {
		font-size: 10px;
		color: var(--muted);
	}
}

.deviation-card__meta {
	display: flex;
	gap: 12px;
	font-size: 12px;
	color: var(--muted);
	flex-wrap: wrap;

	strong {
		color: var(--text);
		font-weight: 600;

		&.up {
			color: #dc2626;
		}
		&.down {
			color: #2563eb;
		}
		&.normal {
			color: #16a34a;
		}
	}
}

@media (max-width: 1240px) {
	.deviation-grid {
		grid-template-columns: repeat(2, 1fr);
	}
}

@media (max-width: 900px) {
	.deviation-grid {
		grid-template-columns: 1fr;
	}
}
</style>
